import { create } from 'zustand';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number; // ms, default 5000
  createdAt: number;
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newNotification: Notification = {
      ...notification,
      id,
      createdAt: Date.now(),
      duration: notification.duration || 5000,
    };

    set((state) => ({
      notifications: [...state.notifications, newNotification],
    }));

    // Auto-remove après la durée spécifiée
    if ((newNotification.duration ?? 0) > 0) {
      setTimeout(() => {
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        }));
      }, newNotification.duration);
    }

    return id;
  },

  removeNotification: (id) =>
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    })),

  clearAll: () => set({ notifications: [] }),
}));

// Fonctions utilitaires pour faciliter l'utilisation
export const notify = {
  success: (message: string, title?: string, duration?: number) =>
    useNotificationStore.getState().addNotification({
      type: 'success',
      message,
      title,
      duration,
    }),

  error: (message: string, title?: string, duration?: number) =>
    useNotificationStore.getState().addNotification({
      type: 'error',
      message,
      title,
      duration,
    }),

  warning: (message: string, title?: string, duration?: number) =>
    useNotificationStore.getState().addNotification({
      type: 'warning',
      message,
      title,
      duration,
    }),

  info: (message: string, title?: string, duration?: number) =>
    useNotificationStore.getState().addNotification({
      type: 'info',
      message,
      title,
      duration,
    }),
};
